import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  ClipboardCheck,
  X,
  Search,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Calendar,
  FileText,
  ArrowRight,
  DollarSign,
  Camera,
  Image as ImageIcon,
  Trash2,
  Ban,
  FolderKanban,
  Printer,
  Lock,
  RotateCw,
  ExternalLink,
  Truck,
  History
} from 'lucide-react';
import {
  OABReviewDetails,
  OABReviewLine,
  fetchPendingOABs,
  fetchOABDetails,
  submitOABReview,
  cancelOAB,
  uploadEvidenceToR2,
  fetchRampReceptions
} from '../services/oabService';
import { OrderSearchModal } from './OrderSearchModal';
import { PrintSheetOAB } from './PrintSheetOAB';
import { ErrorBoundary } from './ErrorBoundary';
import { OrderReference, OABHeader, OABLineItem, computeOABUrgencySummary, computeOABStoresSummary } from '../types/oab';
import { OABStageNotifyPanel } from './OABStageNotifyPanel';


type OABStatusFilter = 'ALL' | 'DRAFTS' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELED';

interface OABReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  bcvRate: number;
  initialFolio?: string;
  onReviewSuccess?: () => void;
  onReorderRejected?: (rejectedLines: OABReviewLine[], sourceFolio: string) => void;
  auth?: any;
}

export const OABReviewModal: React.FC<OABReviewModalProps> = ({
  isOpen,
  onClose,
  bcvRate,
  initialFolio,
  onReviewSuccess,
  onReorderRejected,
  auth
}) => {
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [selectedFolio, setSelectedFolio] = useState(initialFolio || '');
  const [manualFolioInput, setManualFolioInput] = useState(initialFolio || '');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [details, setDetails] = useState<OABReviewDetails | null>(null);

  // Form Fields
  const [proveedorNombre, setProveedorNombre] = useState('');
  const [numeroCotizacion, setNumeroCotizacion] = useState('');
  const [fechaEstimadaEntrega, setFechaEstimadaEntrega] = useState('');
  const [notasCompras, setNotasCompras] = useState('');
  const [lines, setLines] = useState<OABReviewLine[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<any | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Comprobante firmado de Magaly (Hoja Viajera Carta)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Modal y estado de Anulación
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Selector de Obra / Pedido MTO (Fase 11.3)
  const [activeLineForOrder, setActiveLineForOrder] = useState<string | null>(null);
  const [isOrderSearchOpen, setIsOrderSearchOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);

  // Token de control para mitigar carreras asíncronas
  const activeRequestRef = useRef<string>('');

  // Permisos RBAC y Capacidades (PLAN-035)
  const isSuperadmin = Boolean(auth?.profile?.permissions?.includes('Superadmin'));
  const canCotizar = Boolean(auth?.canCotizarOAB ?? true);
  const canAprobar = Boolean(auth?.canAprobarOAB || isSuperadmin);
  const canDelegar = Boolean(auth?.canDelegarOAB && !canAprobar);
  const canVerCostos = Boolean(auth?.canVerCostos ?? true);

  // Estados de Delegación Operativa y Réplica de Proveedor (PLAN-035)
  const [showDelegationModal, setShowDelegationModal] = useState(false);
  const [delegationReason, setDelegationReason] = useState('');
  const [vendorReplicaToast, setVendorReplicaToast] = useState<string | null>(null);

  // Estados para Filtros Históricos (PLAN-015 - Fase 2)
  const [statusFilter, setStatusFilter] = useState<OABStatusFilter>('ALL');

  // Estados para Reimpresión Carta Bimodal (PLAN-015 - Fase 3)
  const [showPrintSheet, setShowPrintSheet] = useState(false);
  const [printIsApprovedVersion, setPrintIsApprovedVersion] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [routeSheetPrintedInReview, setRouteSheetPrintedInReview] = useState(false);

  // Historial contextual de Notas de Recepción Interna (NRI) asociadas a esta OAB (PLAN-048)
  const [nriReceptionsList, setNriReceptionsList] = useState<any[]>([]);
  const [loadingNriReceptions, setLoadingNriReceptions] = useState(false);

  // Conteo de órdenes por estado
  const orderCounts = useMemo(() => {
    const counts = {
      ALL: pendingOrders.length,
      DRAFTS: 0,
      PENDING: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      CANCELED: 0
    };
    pendingOrders.forEach(o => {
      const st = (o.estadoGeneral || '').toLowerCase();
      if (st.includes('borrador') || st.includes('draft')) {
        counts.DRAFTS++;
      } else if (st.includes('solicitad') || st.includes('cotizada')) {
        counts.PENDING++;
      } else if (st.includes('recibido') || st.includes('complet')) {
        counts.COMPLETED++;
      } else if (st.includes('anula') || st.includes('cancel')) {
        counts.CANCELED++;
      } else {
        counts.IN_PROGRESS++;
      }
    });
    return counts;
  }, [pendingOrders]);

  // Lista de órdenes filtradas
  const filteredOrders = useMemo(() => {
    if (statusFilter === 'ALL') return pendingOrders;
    return pendingOrders.filter(o => {
      const st = (o.estadoGeneral || '').toLowerCase();
      if (statusFilter === 'DRAFTS') return st.includes('borrador') || st.includes('draft');
      if (statusFilter === 'PENDING') return st.includes('solicitad') || st.includes('cotizada');
      if (statusFilter === 'IN_PROGRESS') {
        return !st.includes('borrador') && !st.includes('draft') && !st.includes('solicitad') && !st.includes('cotizada') && !st.includes('recibido') && !st.includes('complet') && !st.includes('anula') && !st.includes('cancel');
      }
      if (statusFilter === 'COMPLETED') return st.includes('recibido') || st.includes('complet');
      if (statusFilter === 'CANCELED') return st.includes('anula') || st.includes('cancel');
      return true;
    });
  }, [pendingOrders, statusFilter]);

  // Inmutabilidad Odoo 18 (PLAN-034 / PLAN-035): Modo Bloqueado / Solo Lectura (PO Locked)
  const isReadOnly = useMemo(() => {
    if (!details) return false;
    const st = (details.oab.estadoGeneral || '').toLowerCase();
    // Solo es editable si está en fase inicial de borrador, recién solicitada a gerencia, o cotizada
    const isDocEditable = st.includes('solicitad') || st.includes('borrador') || st.includes('draft') || st.includes('cotizada');
    // Si el usuario no tiene permisos de cotizar, aprobar ni delegar, es modo solo lectura para él
    const userCanEdit = canCotizar || canAprobar || canDelegar;
    return !isDocEditable || !userCanEdit;
  }, [details, canCotizar, canAprobar, canDelegar]);

  // Líneas rechazadas o tachadas (PLAN-034): Cantidad aprobada = 0 o Estado Flujo = Cancelada
  const rejectedLines = useMemo(() => {
    if (!lines || lines.length === 0) return [];
    return lines.filter(l => l.cantidadAprobada === 0 || l.estadoFlujo === 'Cancelada' || l.estadoFlujo === 'Rechazada');
  }, [lines]);

  // Manejo accesible de tecla Escape (PLAN-022)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCancelModal) {
          setShowCancelModal(false);
          return;
        }
        if (showVersionModal) {
          setShowVersionModal(false);
          return;
        }
        if (showPrintSheet) {
          setShowPrintSheet(false);
          return;
        }
        if (isOrderSearchOpen) {
          setIsOrderSearchOpen(false);
          return;
        }
        if (isSubmitting || isCanceling) return;

        const isDirty = proveedorNombre.trim().length > 0 || 
                        numeroCotizacion.trim().length > 0 || 
                        notasCompras.trim().length > 0 || 
                        photoPreview !== null;
        if (isDirty) {
          if (window.confirm('¿Deseas descartar los cambios en la revisión de la OAB?')) {
            onClose();
          }
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen, 
    showCancelModal, 
    showVersionModal, 
    showPrintSheet, 
    isOrderSearchOpen, 
    isSubmitting, 
    isCanceling, 
    proveedorNombre, 
    numeroCotizacion, 
    notasCompras, 
    photoPreview, 
    onClose
  ]);

  // Manejador de Reimpresión Carta
  const handleRequestPrint = () => {
    if (!details) return;
    const isApproved = details.oab.estadoGeneral !== 'Solicitado';
    if (isApproved) {
      setShowVersionModal(true);
    } else {
      setPrintIsApprovedVersion(false);
      setRouteSheetPrintedInReview(true);
      setShowPrintSheet(true);
    }
  };

  const handleSelectPrintVersion = (approved: boolean) => {
    setShowVersionModal(false);
    setPrintIsApprovedVersion(approved);
    setRouteSheetPrintedInReview(true);
    setShowPrintSheet(true);
  };

  const printHeader: OABHeader | null = useMemo(() => {
    if (!details) return null;
    return {
      folio: details.oab.folio,
      fechaEmision: details.oab.fechaEmision,
      tasaBCV: details.oab.tasaBCV || bcvRate || 880.00,
      totalUSD: details.oab.totalUSD,
      totalBs: details.oab.totalBs,
      estadoGeneral: details.oab.estadoGeneral,
      proveedor: proveedorNombre || details.oab.proveedor,
      cotizacion: numeroCotizacion || details.oab.cotizacion,
      fechaEstimadaEntrega: fechaEstimadaEntrega || details.oab.fechaEstimadaEntrega,
      notas: details.oab.notas,
      notasCompras: notasCompras || details.oab.notasCompras,
      isMotherOAB: details.oab.isMotherOAB,
      isSubOrden: details.oab.isSubOrden,
      subOrdenes: details.oab.subOrdenes
    };
  }, [details, proveedorNombre, numeroCotizacion, fechaEstimadaEntrega, notasCompras, bcvRate]);

  const printLines: OABLineItem[] = useMemo(() => {
    if (!lines || lines.length === 0) return [];
    return lines.map(l => ({
      nombre: l.nombre,
      insumoId: l.insumoId || l.solicitudId,
      dashboardId: l.dashboardId || l.solicitudId,
      proveedor: l.proveedorAdjudicado || l.proveedorNombre || l.proveedor,
      proveedorAdjudicado: l.proveedorAdjudicado || l.proveedorNombre || l.proveedor,
      subOrdenFolio: l.subOrdenFolio,
      subOrdenId: l.subOrdenId,
      cantidadStock: 0,
      stockMinimo: 0,
      deficit: 0,
      cantidadSugerida: l.cantidadSolicitada,
      cantidadSolicitada: l.cantidadSolicitada,
      cantidadAprobada: l.cantidadAprobada,
      costoUnitarioUSD: l.costoUnitarioUSD,
      subtotalUSD: l.subtotalUSD,
      prioridad: (l.prioridad as any) || 'Media',
      pedidoId: l.pedidoId,
      proyectoId: l.proyectoId,
      proyectoNombre: l.proyectoNombre
    }));
  }, [lines]);

  // Cargar lista de órdenes pendientes al abrir
  useEffect(() => {
    if (!isOpen) return;
    setSubmitSuccess(null);
    setCancelSuccess(null);
    setErrorMessage(null);
    setDetails(null);
    setPhotoPreview(null);
    setShowCancelModal(false);
    setCancelReason('');
    const targetFolio = (initialFolio || '').trim();
    setSelectedFolio(targetFolio);
    setManualFolioInput(targetFolio);

    setLoadingOrders(true);
    fetchPendingOABs().then(orders => {
      setPendingOrders(orders);
      if (targetFolio) {
        handleSelectFolio(targetFolio);
      } else if (orders.length > 0) {
        handleSelectFolio(orders[0].folio);
      }
    }).finally(() => setLoadingOrders(false));
  }, [isOpen, initialFolio]);

  const handleSelectFolio = async (folioToLoad: string) => {
    if (!folioToLoad) return;
    const cleanFolio = folioToLoad.trim();
    setSelectedFolio(cleanFolio);
    setManualFolioInput(cleanFolio);
    setRouteSheetPrintedInReview(false);
    setNriReceptionsList([]);
    setLoadingDetails(true);
    setErrorMessage(null);
    activeRequestRef.current = cleanFolio;

    // Cargar historial contextual de recepciones NRI de esta OAB (PLAN-048)
    setLoadingNriReceptions(true);
    fetchRampReceptions({ folioOAB: cleanFolio })
      .then((nris) => {
        if (activeRequestRef.current === cleanFolio) {
          setNriReceptionsList(Array.isArray(nris) ? nris : []);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (activeRequestRef.current === cleanFolio) {
          setLoadingNriReceptions(false);
        }
      });

    try {
      const data = await fetchOABDetails(cleanFolio);
      // Descartar si el usuario seleccionó otra orden mientras respondía Notion
      if (activeRequestRef.current !== cleanFolio) {
        return;
      }
      if (data) {
        setDetails(data);
        setProveedorNombre(data.oab.proveedor || '');
        setNumeroCotizacion(data.oab.cotizacion || '');
        setFechaEstimadaEntrega(data.oab.fechaEstimadaEntrega || '');
        setNotasCompras(data.oab.notasCompras || '');
        setLines(data.lines);
      }
    } catch (err: any) {
      if (activeRequestRef.current === cleanFolio) {
        setErrorMessage(err.message || 'Error cargando detalles de la OAB');
        setDetails(null);
      }
    } finally {
      if (activeRequestRef.current === cleanFolio) {
        setLoadingDetails(false);
      }
    }
  };

  const handleLineQtyChange = (solicitudId: string, newQty: number) => {
    setLines(prev => prev.map(l => {
      if (l.solicitudId !== solicitudId) return l;
      const validQty = Math.max(0, newQty);
      return {
        ...l,
        cantidadAprobada: validQty,
        subtotalUSD: validQty * (l.costoUnitarioUSD || 0)
      };
    }));
  };

  const handleLineVendorChange = (solicitudId: string, newVendor: string) => {
    setLines(prev => prev.map(l => {
      if (l.solicitudId !== solicitudId) return l;
      return {
        ...l,
        proveedorNombre: newVendor,
        proveedor: newVendor,
        proveedorAdjudicado: newVendor
      };
    }));
  };

  const handleLineCostChange = (solicitudId: string, newCost: number) => {
    setLines(prev => prev.map(l => {
      if (l.solicitudId !== solicitudId) return l;
      const validCost = Math.max(0, newCost);
      return {
        ...l,
        costoUnitarioUSD: validCost,
        subtotalUSD: (l.cantidadAprobada || 0) * validCost
      };
    }));
  };

  const handleApproveAll = () => {
    setLines(prev => prev.map(l => ({
      ...l,
      cantidadAprobada: l.cantidadSolicitada,
      subtotalUSD: l.cantidadSolicitada * (l.costoUnitarioUSD || 0)
    })));
  };

  // PLAN-064 (P64-D2): Desglose en 1 Clic de Excedente por Sobre-Aprobación Comercial (MTO Tienda + MTS Almacén)
  const handleSplitOverApprovedToMts = (solicitudId: string) => {
    setLines(prev => {
      const idx = prev.findIndex(l => l.solicitudId === solicitudId);
      if (idx === -1) return prev;
      const line = prev[idx];
      const reqQty = Number(line.cantidadSolicitada) || 0;
      const aprQty = Number(line.cantidadAprobada) || 0;
      if (aprQty <= reqQty || reqQty <= 0) return prev;

      const excessQty = aprQty - reqQty;
      const unitCost = Number(line.costoUnitarioUSD) || 0;
      const rootSolId = (line as any).parentSolicitudId || String(line.solicitudId).replace(/^NEW_MTS_/, '').split('_')[0];
      const existingMtsSplits = prev.filter(x => String(x.solicitudId).startsWith(`NEW_MTS_${rootSolId}`)).length;
      const newSyntheticId = `NEW_MTS_${rootSolId}_${existingMtsSplits + 1}`;

      const updatedMtoLine = {
        ...line,
        cantidadAprobada: reqQty,
        subtotalUSD: reqQty * unitCost,
        isReviewed: true
      };

      const newMtsLine = {
        ...line,
        solicitudId: newSyntheticId,
        parentSolicitudId: rootSolId,
        isExcessMtsSplit: true,
        cantidadSolicitada: 0,
        cantidadAprobada: excessQty,
        cantidadRecibidaPrevia: 0,
        backorderPendiente: excessQty,
        subtotalUSD: excessQty * unitCost,
        pedidoId: undefined,
        proyectoId: undefined,
        proyectoNombre: undefined,
        isReviewed: true
      } as any;

      const copy = [...prev];
      copy[idx] = updatedMtoLine;
      copy.splice(idx + 1, 0, newMtsLine);
      return copy;
    });
  };

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setPhotoPreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancelOAB = async () => {
    if (!details) return;
    if (!cancelReason.trim() || cancelReason.trim().length < 5) {
      alert('Debe especificar un motivo justificativo de al menos 5 caracteres.');
      return;
    }

    setIsCanceling(true);
    try {
      await cancelOAB({
        folioOAB: details.oab.folio,
        oabId: details.oab.id,
        motivoCancelacion: cancelReason.trim()
      });
      setCancelSuccess(`La orden ${details.oab.folio} fue anulada formalmente.`);
      setShowCancelModal(false);
      if (onReviewSuccess) onReviewSuccess();
    } catch (err: any) {
      alert(`Error anulando OAB: ${err.message}`);
    } finally {
      setIsCanceling(false);
    }
  };

  const effectiveBcvRate = details?.oab.tasaBCV || bcvRate || 886.23;
  const allLinesZero = lines.length > 0 && lines.every(l => (l.cantidadAprobada || 0) === 0);
  const totalAprobadoUSD = lines.reduce((acc, l) => acc + (l.subtotalUSD || 0), 0);
  const totalAprobadoBs = totalAprobadoUSD * effectiveBcvRate;

  const handleReplicateVendorToAll = () => {
    if (!proveedorNombre.trim()) {
      alert('Por favor ingrese el nombre del proveedor en el campo "Proveedor Adjudicado" antes de replicarlo.');
      return;
    }
    const vendor = proveedorNombre.trim();
    setLines(prev => prev.map(l => ({
      ...l,
      proveedorNombre: vendor,
      proveedor: vendor,
      proveedorAdjudicado: vendor
    })));
    setVendorReplicaToast(`Proveedor "${vendor}" asignado a los ${lines.length} renglones de la orden.`);
    setTimeout(() => setVendorReplicaToast(null), 3500);
  };

  // PLAN-039 / Detección y desglose de adjudicación multiproveedor (Odoo Split Pattern)
  const vendorBreakdown = useMemo(() => {
    const groups: { [key: string]: { count: number; totalUSD: number; lines: OABReviewLine[] } } = {};
    lines.forEach(l => {
      const cantApr = Number(l.cantidadAprobada ?? 0);
      if (cantApr <= 0) return; // Renglones tachados no computan para compras
      const v = (l.proveedorNombre || l.proveedor || proveedorNombre || '').trim() || 'Proveedor Sin Asignar';
      if (!groups[v]) {
        groups[v] = { count: 0, totalUSD: 0, lines: [] };
      }
      groups[v].count++;
      groups[v].totalUSD += (l.subtotalUSD || 0);
      groups[v].lines.push(l);
    });
    return groups;
  }, [lines, proveedorNombre]);

  const distinctVendorCount = Object.keys(vendorBreakdown).length;

  const handleSubmitWithAction = async (action: 'SAVE_QUOTES' | 'APPROVE_PURCHASE', motivoDelegacion?: string) => {
    if (!details) return;

    if (action === 'APPROVE_PURCHASE' && allLinesZero) {
      setErrorMessage('No puede pasar a "En Compra" una orden con todas las líneas aprobadas en cero. Si la gerencia rechazó la requisición, anule la OAB formalmente.');
      return;
    }

    if (action === 'SAVE_QUOTES') {
      const hasAnyVendor = proveedorNombre.trim() || lines.some(l => (l.proveedorNombre || l.proveedor || '').trim().length > 0);
      if (!hasAnyVendor) {
        setErrorMessage('Debe indicar al menos un proveedor (en la cabecera o en los renglones) para guardar la cotización.');
        return;
      }
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      let r2Url: string | undefined = undefined;
      if (photoPreview) {
        try {
          r2Url = await uploadEvidenceToR2(details.oab.folio, action === 'SAVE_QUOTES' ? 'COTIZACION' : 'HOJA-MAGALY', photoPreview);
        } catch (uploadErr) {
          console.warn('Subida de foto a R2 no completada, continuando con revisión:', uploadErr);
        }
      }

      const payload = {
        action,
        oabId: details.oab.id,
        folioOAB: details.oab.folio,
        tasaBCV: effectiveBcvRate,
        proveedorNombre: proveedorNombre.trim(),
        numeroCotizacion: numeroCotizacion.trim(),
        fechaEstimadaEntrega: fechaEstimadaEntrega || undefined,
        notasCompras: notasCompras.trim(),
        motivoDelegacion: motivoDelegacion?.trim() || undefined,
        comprobanteUrl: r2Url,
        lineas: lines.map(l => ({
          solicitudId: l.solicitudId,
          dashboardId: l.dashboardId,
          insumoId: l.insumoId,
          nombre: l.nombre,
          proveedorNombre: (l.proveedorNombre || l.proveedor || proveedorNombre || '').trim(),
          proveedor: (l.proveedorNombre || l.proveedor || proveedorNombre || '').trim(),
          pedidoId: l.pedidoId,
          proyectoId: l.proyectoId,
          proyectoNombre: l.proyectoNombre,
          cantidadSolicitada: l.cantidadSolicitada,
          // DECISIÓN D2 (/grill-me): 0 es rechazo inmutable (tachado), NUNCA resucitar a cantidadSolicitada
          cantidadAprobada: Number(l.cantidadAprobada ?? 0),
          costoUnitarioUSD: l.costoUnitarioUSD || 0,
          rutaLogistica: l.rutaLogistica,
          orderLineId: l.orderLineId,
          isExcessMtsSplit: Boolean((l as any).isExcessMtsSplit || String(l.solicitudId || '').startsWith('NEW_MTS_')),
          parentSolicitudId: (l as any).parentSolicitudId
        }))
      };

      const res = await submitOABReview(payload);
      setSubmitSuccess(res);
      if (onReviewSuccess) onReviewSuccess();
    } catch (err: any) {
      setErrorMessage(err.message || 'Error guardando datos de la OAB');
    } finally {
      setIsSubmitting(false);
      setShowDelegationModal(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-4 no-print">
      <div className="bg-surface border border-borderSubtle rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
        
        {/* Header */}
        <div className="px-5 py-3 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/40">
              <ClipboardCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-100 tracking-tight flex items-center gap-2">
                <span>Puente Papel-Digital: Revisión & Transcripción OAB</span>
                <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  GERENCIA & COMPRAS
                </span>
                {details?.oab?.isTasaFrozen ? (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30" title={`Tasa oficial BCV congelada en orden (Cot: ${details.oab.tasaBCVCotizacion || 'N/A'}, Liq: ${details.oab.tasaBCVLiquidacion || 'N/A'})`}>
                    🔒 BCV: Bs {effectiveBcvRate.toFixed(2)}
                  </span>
                ) : (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" title="Tasa oficial BCV viva de hoy">
                    🟢 BCV: Bs {effectiveBcvRate.toFixed(2)}
                  </span>
                )}
              </h2>
              <p className="text-[11px] text-slate-400">
                Digitalización de vistos buenos de Magaly y registro de cotizaciones antes de la descarga en rampa
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-md hover:bg-surfaceHighest transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {cancelSuccess ? (
          // Vista de Anulación Exitosa
          <div className="p-8 text-center space-y-4 max-w-lg mx-auto my-auto">
            <div className="w-16 h-16 rounded-full bg-red-500/20 text-red-400 border border-red-500/40 flex items-center justify-center mx-auto">
              <Ban className="w-10 h-10" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-100">
                Orden Anulada con Éxito
              </h3>
              <p className="text-xs text-slate-300 mt-1 font-mono">
                {cancelSuccess}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                La orden y sus renglones quedaron cancelados en Notion, se liberó el inventario y se emitió la alerta a Telegram.
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-surfaceHigh hover:bg-surfaceHighest text-slate-200 font-bold rounded-lg text-xs"
            >
              Cerrar y Volver al Dashboard
            </button>
          </div>
        ) : submitSuccess ? (
          // Vista de Éxito o Sincronización Parcial
          <div className="p-8 text-center space-y-4 max-w-lg mx-auto my-auto">
            <div className={`w-16 h-16 rounded-full border flex items-center justify-center mx-auto ${
              submitSuccess.hasSyncErrors
                ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
            }`}>
              {submitSuccess.hasSyncErrors ? (
                <AlertTriangle className="w-10 h-10" />
              ) : (
                <CheckCircle2 className="w-10 h-10" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-100">
                {submitSuccess.hasSyncErrors
                  ? '⚠️ Sincronización Parcial con Notion'
                  : submitSuccess.action === 'SAVE_QUOTES'
                  ? '¡Cotización Guardada con Éxito!'
                  : "¡Orden Transcrita y Puesta 'En Compra'!"}
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Folio <span className="font-mono text-brand-400 font-bold">{submitSuccess.folioOAB}</span> actualizado en Notion. Total: ${(Number(submitSuccess.totalAprobadoUSD ?? submitSuccess.totalEstimadoUSD ?? 0)).toFixed(2)} USD.
                {submitSuccess.tasaBCV && (
                  <span className="ml-1 text-slate-500 font-mono">
                    (Bs {(Number(submitSuccess.totalAprobadoUSD ?? submitSuccess.totalEstimadoUSD ?? 0) * submitSuccess.tasaBCV).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} @ {submitSuccess.tasaBCV})
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                {submitSuccess.hasSyncErrors
                  ? `Se sincronizaron ${submitSuccess.lineasActualizadas} renglones exitosamente. ${submitSuccess.failedCount} renglón(es) tuvieron demoras y quedaron pendientes para reintento.`
                  : submitSuccess.action === 'SAVE_QUOTES'
                  ? 'Precios, proveedor y tasa BCV congelados. Use los botones inferiores para notificar a Gerencia en Telegram o WhatsApp.'
                  : 'La orden está en compra con tasa BCV congelada y rampa habilitada. Use los botones inferiores para notificar a Compras y Almacén.'}
              </p>

              {/* PLAN-039: Sub-Órdenes Hijas Creadas en Aprobación Multiproveedor */}
              {submitSuccess.isSplit && submitSuccess.subOrdenes && submitSuccess.subOrdenes.length > 0 && (
                <div className="mt-3 p-3 bg-indigo-950/40 border border-indigo-500/40 rounded-lg text-left text-xs space-y-2">
                  <div className="font-bold text-indigo-300 flex items-center justify-between">
                    <span>🔀 Sub-Órdenes Hijas Creadas ({submitSuccess.subOrdenes.length}):</span>
                    <span className="text-[10px] bg-indigo-500/20 px-2 py-0.5 rounded text-indigo-300 font-mono">
                      Desacople Odoo 18
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {submitSuccess.subOrdenes.map((sub: any) => (
                      <div key={sub.id} className="bg-surface/80 p-2 rounded border border-borderSubtle flex items-center justify-between">
                        <div>
                          <span className="font-mono font-bold text-brand-300 mr-2">{sub.folio}</span>
                          <span className="text-slate-200 font-medium">🏢 {sub.proveedor}</span>
                          <span className="text-[11px] text-slate-500 ml-2">({sub.lineCount} renglones)</span>
                        </div>
                        <span className="font-mono text-emerald-400 font-bold">
                          ${Number(sub.totalUSD || 0).toFixed(2)} USD
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PLAN-045: Panel Semi-Automático Multicanal (Etapas 2 y 3) */}
              <div className="mt-4 text-left max-w-2xl mx-auto">
                <OABStageNotifyPanel
                  stage={submitSuccess.action === 'SAVE_QUOTES' ? 'QUOTED' : 'APPROVED'}
                  folio={submitSuccess.folioOAB || details?.oab.folio || ''}
                  oabId={details?.oab.id}
                  urgencySummary={details?.oab.nivelUrgenciaGlobal || computeOABUrgencySummary(lines)}
                  storesSummary={details?.oab.resumenTiendasClientes || computeOABStoresSummary(lines)}
                  linesCount={lines.length}
                  approvedLinesCount={lines.filter(l => (l.cantidadAprobada || 0) > 0).length}
                  rejectedLinesCount={lines.filter(l => (l.cantidadAprobada || 0) <= 0).length}
                  operatorName={auth?.profile?.name || auth?.profile?.nombre || 'Operador Autorizado'}
                  operatorRole={auth?.profile?.puestos?.[0] || (submitSuccess.action === 'SAVE_QUOTES' ? 'Compras' : 'Gerencia')}
                  vendorName={proveedorNombre || details?.oab.proveedor || (distinctVendorCount >= 2 ? `Multiproveedor (${distinctVendorCount} empresas)` : 'Por definir')}
                  quoteNumber={numeroCotizacion || details?.oab.cotizacion}
                  estimatedDeliveryDate={fechaEstimadaEntrega || details?.oab.fechaEstimadaEntrega}
                  notes={notasCompras || details?.oab.notasCompras}
                  subOrders={submitSuccess.subOrdenes}
                  delegationReason={delegationReason || undefined}
                />
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-brand-500 hover:bg-brand-400 text-slate-950 font-bold rounded-lg text-xs"
            >
              Cerrar y Volver al Dashboard
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Barra Superior con Chips de Filtro y Selección de OAB */}
            <div className="p-3 bg-page rounded-lg border border-borderSubtle space-y-2.5">
              {/* Chips de Filtro Histórico (PLAN-015 - Fase 2) */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar text-xs">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 shrink-0">Filtrar:</span>
                <button
                  type="button"
                  onClick={() => setStatusFilter('ALL')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition flex items-center gap-1.5 shrink-0 ${
                    statusFilter === 'ALL'
                      ? 'bg-brand-500 text-slate-950 shadow-sm'
                      : 'bg-surfaceHigh text-slate-300 hover:bg-surfaceHighest'
                  }`}
                >
                  <span>Todas</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${statusFilter === 'ALL' ? 'bg-black/20 text-slate-900 font-bold' : 'bg-surface text-slate-400'}`}>
                    {orderCounts.ALL}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('DRAFTS')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition flex items-center gap-1.5 shrink-0 ${
                    statusFilter === 'DRAFTS'
                      ? 'bg-purple-500 text-slate-950 shadow-sm'
                      : 'bg-surfaceHigh text-slate-300 hover:bg-surfaceHighest'
                  }`}
                >
                  <span>Borradores</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${statusFilter === 'DRAFTS' ? 'bg-black/20 text-slate-900 font-bold' : 'bg-surface text-slate-400'}`}>
                    {orderCounts.DRAFTS}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('PENDING')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition flex items-center gap-1.5 shrink-0 ${
                    statusFilter === 'PENDING'
                      ? 'bg-amber-500 text-slate-950 shadow-sm'
                      : 'bg-surfaceHigh text-slate-300 hover:bg-surfaceHighest'
                  }`}
                >
                  <span>Solicitadas</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${statusFilter === 'PENDING' ? 'bg-black/20 text-slate-900 font-bold' : 'bg-surface text-slate-400'}`}>
                    {orderCounts.PENDING}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('IN_PROGRESS')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition flex items-center gap-1.5 shrink-0 ${
                    statusFilter === 'IN_PROGRESS'
                      ? 'bg-sky-500 text-slate-950 shadow-sm'
                      : 'bg-surfaceHigh text-slate-300 hover:bg-surfaceHighest'
                  }`}
                >
                  <span>En Compra / Aprobadas</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${statusFilter === 'IN_PROGRESS' ? 'bg-black/20 text-slate-900 font-bold' : 'bg-surface text-slate-400'}`}>
                    {orderCounts.IN_PROGRESS}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('COMPLETED')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition flex items-center gap-1.5 shrink-0 ${
                    statusFilter === 'COMPLETED'
                      ? 'bg-emerald-500 text-slate-950 shadow-sm'
                      : 'bg-surfaceHigh text-slate-300 hover:bg-surfaceHighest'
                  }`}
                >
                  <span>Recibidas</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${statusFilter === 'COMPLETED' ? 'bg-black/20 text-slate-900 font-bold' : 'bg-surface text-slate-400'}`}>
                    {orderCounts.COMPLETED}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('CANCELED')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition flex items-center gap-1.5 shrink-0 ${
                    statusFilter === 'CANCELED'
                      ? 'bg-rose-500 text-slate-950 shadow-sm'
                      : 'bg-surfaceHigh text-slate-300 hover:bg-surfaceHighest'
                  }`}
                >
                  <span>Anuladas</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${statusFilter === 'CANCELED' ? 'bg-black/20 text-slate-900 font-bold' : 'bg-surface text-slate-400'}`}>
                    {orderCounts.CANCELED}
                  </span>
                </button>
              </div>

              {/* Fila de Selección y Búsqueda */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-borderSubtle/60">
                <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                  <label htmlFor="rev_selected_folio" className="text-xs text-slate-400 font-semibold whitespace-nowrap">
                    Seleccionar OAB:
                  </label>
                  <select
                    id="rev_selected_folio"
                    name="rev_selected_folio"
                    value={selectedFolio}
                    onChange={(e) => handleSelectFolio(e.target.value)}
                    className="flex-1 bg-surface border border-borderSubtle text-brand-400 font-mono text-xs px-2.5 py-1.5 rounded focus:outline-none focus:border-brand-400"
                    disabled={loadingOrders || loadingDetails}
                  >
                    {filteredOrders.length === 0 ? (
                      <option value="">No hay órdenes en este filtro ({statusFilter})</option>
                    ) : (
                      filteredOrders.map(o => (
                        <option key={o.id} value={o.folio}>
                          {o.folio} — {o.estadoGeneral} ({o.fechaEmision}) · ${Number(o.totalUSD || 0).toFixed(2)} USD
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="rev_manual_folio"
                    name="rev_manual_folio"
                    type="text"
                    placeholder="Digitar folio o escanear QR..."
                    aria-label="Digitar folio o escanear QR"
                    value={manualFolioInput}
                    onChange={(e) => setManualFolioInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !loadingDetails && handleSelectFolio(manualFolioInput)}
                    disabled={loadingOrders || loadingDetails}
                    className="bg-surface border border-borderSubtle text-xs px-2.5 py-1.5 rounded font-mono text-slate-200 w-44 disabled:opacity-50 focus:outline-none focus:border-brand-400"
                  />
                  <button
                    type="button"
                    onClick={() => handleSelectFolio(manualFolioInput)}
                    disabled={loadingOrders || loadingDetails}
                    className="px-3 py-1.5 bg-surfaceHigh hover:bg-surfaceHighest disabled:opacity-50 text-slate-300 text-xs rounded border border-borderSubtle flex items-center gap-1 transition"
                  >
                    <Search className="w-3.5 h-3.5" />
                    <span>Buscar</span>
                  </button>

                  {/* Botón Directo de Reimpresión Carta (PLAN-015) */}
                  <button
                    type="button"
                    onClick={handleRequestPrint}
                    disabled={!details || loadingDetails}
                    className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-xs font-bold rounded flex items-center gap-1.5 transition shadow-sm"
                    title="Reimprimir Hoja Viajera Carta"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Reimprimir</span> Carta
                  </button>
                </div>
              </div>
            </div>

            {loadingDetails && (
              <div className="space-y-4 p-2 animate-pulse">
                {/* Skeleton Header Form */}
                <div className="p-4 bg-surfaceHigh/40 rounded-xl border border-borderSubtle/60 grid grid-cols-1 sm:grid-cols-4 gap-3">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="space-y-2">
                      <div className="h-2.5 bg-slate-700/50 rounded w-24"></div>
                      <div className="h-8 bg-slate-800/80 rounded-lg border border-slate-700/30"></div>
                    </div>
                  ))}
                </div>

                {/* Skeleton Table Lines */}
                <div className="border border-borderSubtle/60 rounded-xl overflow-hidden bg-surface">
                  <div className="h-10 bg-surfaceHigh/60 border-b border-borderSubtle/60 flex items-center justify-between px-4">
                    <div className="h-3 bg-slate-700/60 rounded w-32"></div>
                    <div className="h-3 bg-slate-700/60 rounded w-48"></div>
                  </div>
                  <div className="divide-y divide-borderSubtle/30 p-2 space-y-2">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="flex items-center justify-between gap-4 py-2.5 px-3">
                        <div className="space-y-1.5 flex-1">
                          <div className="h-4 bg-slate-700/60 rounded w-2/5"></div>
                          <div className="h-2.5 bg-slate-800/80 rounded w-1/4"></div>
                        </div>
                        <div className="h-4 bg-slate-700/40 rounded w-16"></div>
                        <div className="h-7 bg-slate-800/90 rounded w-24 border border-slate-700/30"></div>
                        <div className="h-7 bg-slate-800/90 rounded w-20 border border-slate-700/30"></div>
                        <div className="h-4 bg-slate-700/60 rounded w-16"></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center text-[11px] text-slate-500 font-mono">
                  Sincronizando líneas de la orden desde Notion ERP...
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {details && !loadingDetails && (
              <>
                {/* Banner de Documento Bloqueado (Odoo 18 Pattern - PLAN-034) */}
                {isReadOnly && (
                  <div className="p-3 bg-blue-950/40 border border-blue-500/40 rounded-lg flex flex-wrap items-center justify-between gap-3 text-xs text-blue-200">
                    <div className="flex items-center gap-2.5">
                      <div className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30">
                        <Lock className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-100">🔒 Documento Bloqueado (Odoo 18 Pattern)</span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40 font-bold uppercase">
                            {details.oab.estadoGeneral}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-300 mt-0.5">
                          Esta orden ya fue confirmada o inició recepción física en rampa. Las cantidades y costos están protegidos en modo consulta y auditoría.
                        </p>
                      </div>
                    </div>
                    {details.oab.estadoGeneral !== 'Cancelada' && (
                      <a
                        href={`/?rampa=true&folio=${encodeURIComponent(details.oab.folio)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-bold flex items-center gap-1.5 shrink-0 transition shadow-sm"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span>Ver en Terminal de Rampa</span>
                      </a>
                    )}
                  </div>
                )}

                {/* Adjuntar Foto de Hoja Viajera Carta Firmada */}
                <div className="p-3 bg-surfaceHigh/60 rounded-lg border border-borderSubtle flex flex-wrap items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded bg-surface text-amber-400 border border-borderSubtle">
                      <Camera className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-slate-200 font-semibold flex items-center gap-2">
                        <span>Hoja Viajera Carta Firmada (Magaly)</span>
                        {photoPreview && (
                          <span className="text-[10px] text-emerald-400 font-mono bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/30">
                            ✓ Documento Listo
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        Sube o fotografía la hoja física Carta con los vistos buenos manuscritos para auditoría
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {photoPreview ? (
                      <div className="flex items-center gap-2">
                        <img
                          src={photoPreview}
                          alt="Comprobante Magaly"
                          className="w-9 h-9 rounded object-cover border border-amber-500/40 shadow"
                        />
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => setPhotoPreview(null)}
                            className="p-1.5 rounded bg-red-950/40 hover:bg-red-900/50 text-red-400 border border-red-500/40"
                            title="Eliminar foto"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ) : (
                      !isReadOnly && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-3 py-1.5 bg-surface hover:bg-surfaceHighest text-slate-200 text-xs rounded border border-borderSubtle flex items-center gap-1.5 transition"
                        >
                          <Camera className="w-3.5 h-3.5 text-amber-400" />
                          <span>Subir Hoja Firmada</span>
                        </button>
                      )
                    )}
                    <input
                      id="rev_foto_firmada"
                      name="rev_foto_firmada"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      ref={fileInputRef}
                      onChange={handlePhotoCapture}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Banner de confirmación de réplica de proveedor (PLAN-035) */}
                {vendorReplicaToast && (
                  <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-300 flex items-center gap-2 animate-in fade-in">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span>{vendorReplicaToast}</span>
                  </div>
                )}

                {/* Formulario de Compras */}
                <div className="p-3 bg-surfaceHigh/60 rounded-lg border border-borderSubtle grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label htmlFor="rev_proveedor_nombre" className="block text-[10px] uppercase text-slate-400 font-semibold">
                        Proveedor Adjudicado
                      </label>
                      {canCotizar && !isReadOnly && proveedorNombre.trim() && (
                        <button
                          type="button"
                          onClick={handleReplicateVendorToAll}
                          className="text-[10px] text-amber-400 hover:text-amber-300 font-bold flex items-center gap-0.5 hover:underline"
                          title="Replicar este proveedor a todos los renglones de la orden"
                        >
                          ⚡ Replicar
                        </button>
                      )}
                    </div>
                    <input
                      id="rev_proveedor_nombre"
                      name="rev_proveedor_nombre"
                      type="text"
                      placeholder="Ej: Distribuidora Hierros C.A."
                      value={proveedorNombre}
                      readOnly={isReadOnly}
                      onChange={(e) => setProveedorNombre(e.target.value)}
                      className={`w-full bg-surface border border-borderSubtle rounded px-2.5 py-1.5 text-xs ${
                        isReadOnly ? 'opacity-80 bg-surface/50 text-slate-300 cursor-default' : 'text-slate-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label htmlFor="rev_numero_cotizacion" className="block text-[10px] uppercase text-slate-400 font-semibold mb-1">
                      N° Cotización / Pre-Factura
                    </label>
                    <input
                      id="rev_numero_cotizacion"
                      name="rev_numero_cotizacion"
                      type="text"
                      placeholder="Ej: COT-2026-904"
                      value={numeroCotizacion}
                      readOnly={isReadOnly}
                      onChange={(e) => setNumeroCotizacion(e.target.value)}
                      className={`w-full bg-surface border border-borderSubtle rounded px-2.5 py-1.5 text-xs font-mono ${
                        isReadOnly ? 'opacity-80 bg-surface/50 text-slate-300 cursor-default' : 'text-slate-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label htmlFor="rev_fecha_entrega" className="block text-[10px] uppercase text-slate-400 font-semibold mb-1">
                      Fecha Estimada de Entrega
                    </label>
                    <input
                      id="rev_fecha_entrega"
                      name="rev_fecha_entrega"
                      type="date"
                      value={fechaEstimadaEntrega}
                      readOnly={isReadOnly}
                      onChange={(e) => setFechaEstimadaEntrega(e.target.value)}
                      className={`w-full bg-surface border border-borderSubtle rounded px-2.5 py-1.5 text-xs font-mono ${
                        isReadOnly ? 'opacity-80 bg-surface/50 text-slate-300 cursor-default' : 'text-slate-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label htmlFor="rev_notas_compras" className="block text-[10px] uppercase text-slate-400 font-semibold mb-1">
                      Notas Comerciales
                    </label>
                    <input
                      id="rev_notas_compras"
                      name="rev_notas_compras"
                      type="text"
                      placeholder="Condiciones de pago, flete..."
                      value={notasCompras}
                      readOnly={isReadOnly}
                      onChange={(e) => setNotasCompras(e.target.value)}
                      className={`w-full bg-surface border border-borderSubtle rounded px-2.5 py-1.5 text-xs ${
                        isReadOnly ? 'opacity-80 bg-surface/50 text-slate-300 cursor-default' : 'text-slate-200'
                      }`}
                    />
                  </div>
                </div>

                {/* Acciones Rápidas de Aprobación */}
                <div className="flex items-center justify-between text-xs pt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">{isReadOnly ? 'Renglones en esta orden:' : 'Renglones a transcribir:'}</span>
                    <span className="font-mono font-bold text-slate-200">{lines.length}</span>
                    {isReadOnly && (
                      <span className="text-[10px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20 font-mono font-semibold">
                        MODO CONSULTA
                      </span>
                    )}
                  </div>
                  {!isReadOnly && (canAprobar || canDelegar) && (
                    <button
                      onClick={handleApproveAll}
                      className="text-xs text-brand-400 hover:text-brand-300 underline font-medium"
                    >
                      ✓ Aprobar todo tal como se solicitó
                    </button>
                  )}
                </div>

                {/* Tabla de Renglones para Transcripción */}
                <div className="border border-borderSubtle rounded-lg overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-surfaceHigh text-slate-400 border-b border-borderSubtle font-mono text-[11px]">
                      <tr>
                        <th className="py-2 px-3">Insumo</th>
                        <th className="py-2 px-3 text-center">Destino MTO / Obra</th>
                        <th className="py-2 px-3 text-left">Proveedor Adjudicado</th>
                        <th className="py-2 px-3 text-right">Cant. Solicitada</th>
                        <th className="py-2 px-3 text-center">{canCotizar && !canAprobar && !canDelegar ? 'Cant. Requerida' : 'V°B° Magaly (Aprobada)'}</th>
                        <th className="py-2 px-3 text-center">Recibido en Rampa</th>
                        <th className="py-2 px-3 text-center">Pendiente (Backorder)</th>
                        <th className="py-2 px-3 text-right">P. Unit ($)</th>
                        <th className="py-2 px-3 text-right">Subtotal ($)</th>
                        <th className="py-2 px-3 text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-borderSubtle/40 bg-surface">
                      {lines.map((line) => {
                        const isAdjusted = line.cantidadAprobada !== line.cantidadSolicitada;
                        const isRejected = line.cantidadAprobada === 0;

                        return (
                          <tr key={line.solicitudId} className="hover:bg-surfaceHighest/40">
                            <td className="py-2.5 px-3">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-medium text-slate-200">{line.nombre}</span>
                                {(line.rutaLogistica === 'SERVICIO_SITIO' || line.rutaLogistica === 'DIRECTO_OBRA' || line.isDirectOrService) && (
                                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40">
                                    {line.rutaLogistica === 'SERVICIO_SITIO' ? '⚡ Servicio en Sitio' : '🚚 Envío Directo a Obra'}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              {line.proyectoNombre ? (
                                <div className="inline-flex items-center gap-1.5 bg-brand-500/10 border border-brand-500/30 px-2 py-0.5 rounded text-left">
                                  <span className="text-[10px] text-brand-300 font-mono font-bold max-w-[150px] truncate" title={line.proyectoNombre}>
                                    {line.proyectoNombre}
                                  </span>
                                  {!isReadOnly && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setActiveLineForOrder(line.solicitudId);
                                          setIsOrderSearchOpen(true);
                                        }}
                                        className="p-0.5 text-slate-400 hover:text-white rounded hover:bg-surfaceHighest transition"
                                        title="Cambiar obra o pedido"
                                      >
                                        <FolderKanban className="w-3.5 h-3.5 text-brand-400" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setLines(prev => prev.map(l => l.solicitudId === line.solicitudId ? { ...l, pedidoId: undefined, proyectoId: undefined, proyectoNombre: undefined } : l));
                                        }}
                                        className="p-0.5 text-slate-500 hover:text-rose-400 rounded hover:bg-rose-500/10 transition text-xs font-bold"
                                        title="Desvincular obra (pasar a Stock Fábrica / General)"
                                      >
                                        ✕
                                      </button>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-1.5 bg-slate-800/80 border border-slate-700 px-2 py-0.5 rounded">
                                  <span className="text-[10px] text-slate-400 font-mono">
                                    📦 Stock Fábrica
                                  </span>
                                  {!isReadOnly && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActiveLineForOrder(line.solicitudId);
                                        setIsOrderSearchOpen(true);
                                      }}
                                      className="px-1.5 py-0.5 text-[9px] text-brand-400 hover:text-brand-300 rounded border border-brand-500/30 bg-brand-500/10 transition"
                                      title="Asignar a un pedido o tienda"
                                    >
                                      + Asignar
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            {/* Proveedor Adjudicado por Renglón (PLAN-039) */}
                            <td className="py-2.5 px-3">
                              {isReadOnly ? (
                                <div className="font-medium text-slate-300 text-xs flex items-center gap-1.5">
                                  <span className="truncate max-w-[140px]" title={line.proveedorNombre || line.proveedor || proveedorNombre || 'Sin asignar'}>
                                    {line.proveedorNombre || line.proveedor || proveedorNombre || 'Sin asignar'}
                                  </span>
                                  {line.subOrdenFolio && (
                                    <span className="text-[10px] font-mono px-1.5 py-0.2 bg-amber-500/20 text-amber-300 rounded border border-amber-500/30 font-bold shrink-0" title={`Sub-Orden ${line.subOrdenFolio}`}>
                                      {line.subOrdenFolio.split('/')[1] || line.subOrdenFolio}
                                    </span>
                                  )}
                                </div>
                              ) : canCotizar ? (
                                <input
                                  id={`rev_vendor_${line.solicitudId}`}
                                  name={`rev_vendor_${line.solicitudId}`}
                                  type="text"
                                  placeholder={proveedorNombre || "Proveedor..."}
                                  value={line.proveedorNombre || ''}
                                  onChange={(e) => handleLineVendorChange(line.solicitudId, e.target.value)}
                                  className="w-32 sm:w-36 px-2 py-1 text-xs bg-page border border-borderSubtle rounded text-slate-200 placeholder:text-slate-600 focus:border-brand-500"
                                  title="Proveedor específico para este insumo (dejar en blanco para usar el proveedor principal de cabecera)"
                                />
                              ) : (
                                <span className="text-xs text-slate-400 truncate max-w-[140px] block" title={line.proveedorNombre || proveedorNombre || 'Por definir'}>
                                  {line.proveedorNombre || proveedorNombre || 'Por definir'}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-400">
                              {line.cantidadSolicitada}
                            </td>
                            <td className="py-2.5 px-3">
                              {isReadOnly ? (
                                <div className="text-center font-mono">
                                  {isRejected ? (
                                    <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold bg-red-950/50 text-red-400 border border-red-500/40">
                                      0 (Tachado)
                                    </span>
                                  ) : (
                                    <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                                      {line.cantidadAprobada}
                                    </span>
                                  )}
                                </div>
                              ) : canCotizar && !canAprobar && !canDelegar ? (
                                <div className="text-center font-mono" title="La asignación de cantidades aprobadas es potestad exclusiva de Gerencia">
                                  <span className="inline-block px-2.5 py-1 rounded text-xs font-bold bg-slate-800 text-slate-300 border border-slate-700">
                                    {line.cantidadSolicitada} <span className="text-[10px] text-slate-500 font-normal">(Req.)</span>
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-1.5">
                                  {!line.isReviewed && !isRejected && (
                                    <span className="text-[9px] font-mono text-slate-400 bg-slate-800/80 px-1 py-0.5 rounded border border-slate-700/60" title="Valor sugerido por defecto igual a la solicitud">
                                      Sugerido
                                    </span>
                                  )}
                                  <input
                                    id={`rev_qty_${line.solicitudId}`}
                                    name={`rev_qty_${line.solicitudId}`}
                                    aria-label={`Cantidad aprobada para ${line.nombre}`}
                                    type="number"
                                    min="0"
                                    value={line.cantidadAprobada}
                                    onKeyDown={(e) => {
                                      if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                                    }}
                                    onChange={(e) => handleLineQtyChange(line.solicitudId, Math.max(0, Number(e.target.value)))}
                                    className={`w-20 px-2 py-1 text-center font-mono font-bold text-xs rounded border ${
                                      isRejected
                                        ? 'bg-red-950/40 border-red-500/50 text-red-300'
                                        : isAdjusted
                                        ? 'bg-amber-950/40 border-amber-500/50 text-amber-300'
                                        : 'bg-page border-borderSubtle text-emerald-400'
                                    }`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleLineQtyChange(line.solicitudId, line.cantidadSolicitada)}
                                    title="Aprobar todo"
                                    className="px-1.5 py-0.5 text-[10px] bg-surfaceHigh hover:bg-surfaceHighest rounded text-slate-300"
                                  >
                                    Todo
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleLineQtyChange(line.solicitudId, 0)}
                                    title="Tachar / Rechazar"
                                    className="px-1.5 py-0.5 text-[10px] bg-red-900/30 hover:bg-red-800/40 rounded text-red-300"
                                  >
                                    0
                                  </button>
                                </div>
                              )}
                              {!isReadOnly &&
                                line.proyectoNombre &&
                                !line.proyectoNombre.toLowerCase().includes('stock general') &&
                                !line.proyectoNombre.toLowerCase().includes('stock fábrica') &&
                                !line.proyectoNombre.toLowerCase().includes('stock fabrica') &&
                                (line.cantidadSolicitada || 0) > 0 &&
                                (line.cantidadAprobada || 0) > (line.cantidadSolicitada || 0) && (
                                  <div className="mt-1.5 flex justify-center">
                                    <button
                                      type="button"
                                      onClick={() => handleSplitOverApprovedToMts(line.solicitudId)}
                                      className="px-2 py-0.5 text-[10px] font-mono font-bold rounded bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 border border-indigo-500/40 transition active:scale-95"
                                      title={`Dejar ${line.cantidadSolicitada} en la tienda (${line.proyectoNombre}) y separar el excedente (+${(line.cantidadAprobada || 0) - (line.cantidadSolicitada || 0)}) en un renglón para Stock Fábrica (Almacén)`}
                                    >
                                      ✂️ Separar +{(line.cantidadAprobada || 0) - (line.cantidadSolicitada || 0)} a Stock Almacén
                                    </button>
                                  </div>
                                )}
                            </td>
                            {/* Recibido en Rampa (PLAN-034) */}
                            <td className="py-2.5 px-3 text-center font-mono">
                              {(line.cantidadRecibidaPrevia || 0) > 0 ? (
                                <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                  {line.cantidadRecibidaPrevia}
                                </span>
                              ) : (
                                <span className="text-slate-500 text-[11px]">0</span>
                              )}
                            </td>
                            {/* Pendiente / Backorder (PLAN-034) */}
                            <td className="py-2.5 px-3 text-center font-mono">
                              {(line.backorderPendiente || 0) > 0 ? (
                                <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                  {line.backorderPendiente}
                                </span>
                              ) : (
                                <span className="text-slate-500 text-[11px]">0</span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-right">
                              {isReadOnly ? (
                                <span className="font-mono text-xs text-slate-300">
                                  ${(line.costoUnitarioUSD || 0).toFixed(2)}
                                </span>
                              ) : (
                                <input
                                  id={`rev_cost_${line.solicitudId}`}
                                  name={`rev_cost_${line.solicitudId}`}
                                  aria-label={`Costo unitario USD para ${line.nombre}`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={line.costoUnitarioUSD}
                                  onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                                  }}
                                  onChange={(e) => handleLineCostChange(line.solicitudId, Math.max(0, Number(e.target.value)))}
                                  className="w-16 px-2 py-1 text-right font-mono text-xs bg-page border border-borderSubtle rounded text-slate-200"
                                />
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-200">
                              ${line.subtotalUSD.toFixed(2)}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              {isRejected ? (
                                <span className="inline-block px-2 py-0.5 rounded text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 font-medium">
                                  Tachado
                                </span>
                              ) : isAdjusted ? (
                                <span className="inline-block px-2 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30 font-medium">
                                  Ajustado
                                </span>
                              ) : (
                                <span className="inline-block px-2 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-medium">
                                  Aprobado
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* PLAN-039: Sub-Órdenes Activas ya Emitidas (Modo Consulta o Rampa) */}
                {details?.oab?.subOrdenes && details.oab.subOrdenes.length > 0 && (
                  <div className="p-3 bg-emerald-950/20 border border-emerald-500/30 rounded-lg text-xs text-emerald-200 space-y-2">
                    <div className="flex items-center justify-between font-bold text-emerald-300">
                      <span className="flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-emerald-400" />
                        <span>Sub-Órdenes Hijas Emitidas ({details.oab.subOrdenes.length}):</span>
                      </span>
                      <span className="text-[10px] bg-emerald-500/20 px-2 py-0.5 rounded text-emerald-300 font-mono">
                        Desacople Odoo 18
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {details.oab.subOrdenes.map(sub => (
                        <div key={sub.id} className="bg-surface/80 border border-emerald-500/30 p-2.5 rounded flex flex-col justify-between shadow-sm">
                          <div className="flex items-center justify-between font-mono font-bold text-slate-100">
                            <span className="text-brand-400">{sub.folio}</span>
                            <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 font-mono">
                              {sub.estadoGeneral}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-300 truncate mt-1" title={sub.proveedor}>
                            🏢 {sub.proveedor}
                          </div>
                          <div className="text-[11px] font-mono text-emerald-400 font-bold mt-1">
                            ${Number(sub.totalUSD || 0).toFixed(2)} USD
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PLAN-039: Previsualización de Bifurcación Multiproveedor (Modo Cotización / Aprobación) */}
                {!isReadOnly && distinctVendorCount >= 2 && (
                  <div className="p-3 bg-indigo-950/40 border border-indigo-500/40 rounded-lg text-xs text-indigo-200 space-y-2">
                    <div className="flex items-center justify-between font-bold text-indigo-300">
                      <span className="flex items-center gap-1.5">
                        <span>🔀 Bifurcación Multiproveedor Detectada ({distinctVendorCount} Proveedores Adjudicados)</span>
                      </span>
                      <span className="text-[10px] bg-indigo-500/30 px-2 py-0.5 rounded text-indigo-200 font-mono">
                        Al aprobar se crearán {distinctVendorCount} sub-órdenes hijas
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(vendorBreakdown).map(([vendor, vData], idx) => {
                        const letter = String.fromCharCode(65 + idx);
                        return (
                          <div key={vendor} className="bg-surface/90 border border-borderSubtle p-2 rounded flex flex-col justify-between shadow-sm">
                            <div className="font-semibold text-slate-100 flex items-center justify-between">
                              <span className="truncate max-w-[150px]" title={vendor}>🏢 {vendor}</span>
                              <span className="font-mono text-[10px] text-amber-400 font-bold bg-amber-500/10 px-1.5 py-0.2 rounded border border-amber-500/30">
                                Sub-Orden {letter}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400 font-mono">
                              <span>{vData.count} renglones</span>
                              <span className="text-emerald-400 font-bold">${vData.totalUSD.toFixed(2)} USD</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Advertencia preventiva si todas las líneas están en cero (solo en modo editable) */}
                {!isReadOnly && allLinesZero && (
                  <div className="p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-xs text-red-300 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                      <span>Todos los renglones están tachados / en 0. Si la gerencia rechazó la requisición, anule la OAB formalmente.</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCancelModal(true)}
                      className="px-2.5 py-1 bg-red-600 hover:bg-red-500 text-white font-bold rounded text-[11px] flex items-center gap-1 shadow"
                    >
                      <Ban className="w-3.5 h-3.5" />
                      <span>Anular OAB Ahora</span>
                    </button>
                  </div>
                )}

                {/* PLAN-048: Historial Contextual de Recepciones en Muelle (NRI) asociadas a esta OAB */}
                {(nriReceptionsList.length > 0 ||
                  loadingNriReceptions ||
                  ['En Compra', 'Recepción Parcial', 'Recibido Parcial', 'Completada'].includes(details?.oab?.estadoGeneral || '')) && (
                  <div className="p-3 bg-emerald-950/15 border border-emerald-500/30 rounded-lg space-y-2 text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 font-bold text-emerald-300">
                        <Truck className="w-4 h-4 text-emerald-400" />
                        <span>🚚 Historial de Recepciones en Muelle (NRI) — {details?.oab?.folio}</span>
                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-mono text-[10px]">
                          {nriReceptionsList.length} viaje(s)
                        </span>
                      </div>
                      <a
                        href={`/?rampReceipt=${encodeURIComponent(nriReceptionsList[0]?.nriFolio || details?.oab?.folio || '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 border border-emerald-500/30 text-emerald-300 font-semibold text-[11px] transition"
                      >
                        <History className="w-3.5 h-3.5" />
                        <span>Abrir Historial NRI Completo</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>

                    {loadingNriReceptions ? (
                      <div className="text-[11px] text-slate-400 font-mono animate-pulse">
                        Consultando recepciones parciales y completas en BD_Recepciones_Rampa...
                      </div>
                    ) : nriReceptionsList.length === 0 ? (
                      <div className="text-[11px] text-slate-400 italic">
                        Aún no se registran ingresos físicos en muelle (NRI) para esta orden.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {nriReceptionsList.map((nri: any) => (
                          <div
                            key={nri.id || nri.nriFolio}
                            className="p-2.5 rounded bg-surface/90 border border-borderSubtle flex items-center justify-between gap-2"
                          >
                            <div className="space-y-0.5 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-mono font-bold text-emerald-400">{nri.nriFolio}</span>
                                <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-slate-800 text-slate-200 border border-slate-700">
                                  {nri.estadoRecepcion || 'Recibida'}
                                </span>
                                {nri.actaRechazoFolio && (
                                  <span className="px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40">
                                    ⚠️ {nri.actaRechazoFolio}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-400 truncate">
                                📅 {nri.fechaRecepcion} · Guía #{nri.numeroNotaEntrega || 'S/N'}
                                {nri.choferNombre ? ` · 🚚 ${nri.choferNombre}${nri.placaVehiculo ? ` (${nri.placaVehiculo})` : ''}` : ''}
                              </div>
                              <div className="text-[10px] font-mono text-slate-300">
                                ✅ +{nri.totales?.recibidosHoy ?? 0} und ({nri.totales?.bultosHoy ?? 1} bultos)
                                {(nri.totales?.faltantes ?? 0) > 0 && ` · ⏳ Pend: ${nri.totales.faltantes}`}
                                {(nri.totales?.rechazadosHoy ?? 0) > 0 && ` · ❌ Rech: ${nri.totales.rechazadosHoy}`}
                              </div>
                            </div>
                            <a
                              href={`/?rampReceipt=${encodeURIComponent(nri.nriFolio)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2.5 py-1.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 font-mono text-[11px] font-bold shrink-0 flex items-center gap-1 transition"
                              title="Abrir detalle e impresión Carta de esta NRI"
                            >
                              <span>Ver NRI</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* PLAN-045: Panel Semi-Automático de Notificación Multicanal desde Detalle Activo */}
                {details?.oab && ['Solicitado', 'Cotizada', 'En Compra'].includes(details.oab.estadoGeneral) && (
                  <OABStageNotifyPanel
                    compact
                    stage={
                      details.oab.estadoGeneral === 'Solicitado'
                        ? 'EMITTED'
                        : details.oab.estadoGeneral === 'Cotizada'
                        ? 'QUOTED'
                        : 'APPROVED'
                    }
                    folio={details.oab.folio}
                    oabId={details.oab.id}
                    urgencySummary={details.oab.nivelUrgenciaGlobal || computeOABUrgencySummary(lines)}
                    storesSummary={details.oab.resumenTiendasClientes || computeOABStoresSummary(lines)}
                    linesCount={lines.length}
                    approvedLinesCount={lines.filter(l => (l.cantidadAprobada || 0) > 0).length}
                    rejectedLinesCount={lines.filter(l => (l.cantidadAprobada || 0) <= 0).length}
                    routeSheetPrinted={routeSheetPrintedInReview}
                    operatorName={auth?.profile?.name || auth?.profile?.nombre || 'Operador Autorizado'}
                    operatorRole={auth?.profile?.puestos?.[0] || 'Operaciones'}
                    vendorName={proveedorNombre || details.oab.proveedor || (distinctVendorCount >= 2 ? `Multiproveedor (${distinctVendorCount} empresas)` : undefined)}
                    quoteNumber={numeroCotizacion || details.oab.cotizacion}
                    estimatedDeliveryDate={fechaEstimadaEntrega || details.oab.fechaEstimadaEntrega}
                    notes={notasCompras || details.oab.notasCompras}
                  />
                )}

                {/* Resumen de Totales y Botones de Acción */}
                <div className="p-3 bg-page rounded-lg border border-borderSubtle flex flex-wrap items-center justify-between gap-3 text-xs">
                  <div className="space-y-0.5">
                    <div className="text-slate-400">
                      {isReadOnly ? 'Total Presupuestado / Aprobado:' : 'Total Aprobado por Magaly:'}
                    </div>
                    <div className="text-base font-bold text-emerald-400 font-mono">
                      ${totalAprobadoUSD.toFixed(2)} USD
                      <span className="text-xs text-amber-300 font-normal ml-2">
                        (Bs {totalAprobadoBs.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} BCV)
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Botón de Re-solicitud Asistida en Nueva OAB (PLAN-034) */}
                    {rejectedLines.length > 0 && onReorderRejected && (
                      <button
                        type="button"
                        onClick={() => {
                          if (details) {
                            onReorderRejected(rejectedLines, details.oab.folio);
                            onClose();
                          }
                        }}
                        className="px-3.5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 transition shadow active:scale-95"
                        title="Crear una nueva orden de abastecimiento borrador con los insumos rechazados para re-evaluación"
                      >
                        <RotateCw className="w-3.5 h-3.5" />
                        <span>Re-solicitar Rechazados ({rejectedLines.length})</span>
                      </button>
                    )}

                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => setShowCancelModal(true)}
                        className="px-3 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-950/40 border border-red-500/30 rounded flex items-center gap-1.5 transition"
                      >
                        <Ban className="w-3.5 h-3.5" />
                        <span>{details?.oab.estadoGeneral === 'Borrador' ? 'Descartar Borrador' : 'Anular OAB'}</span>
                      </button>
                    )}

                    <button
                      onClick={onClose}
                      className="px-3.5 py-2 text-xs text-slate-400 hover:text-slate-200 rounded hover:bg-surfaceHighest transition"
                    >
                      Cerrar
                    </button>

                    {!isReadOnly ? (
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Botón Compras: Guardar Cotizaciones y Solicitar Aprobación (Task 3.6) */}
                        {canCotizar && (
                          <button
                            type="button"
                            onClick={() => handleSubmitWithAction('SAVE_QUOTES')}
                            disabled={isSubmitting || lines.length === 0}
                            className="px-3.5 py-2 bg-sky-600 hover:bg-sky-500 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 transition disabled:opacity-50 shadow-sm active:scale-95"
                            title="Guarda precios y proveedor sin pasar a tránsito físico. Solicita visto bueno presupuestario a Gerencia."
                          >
                            <FileText className="w-4 h-4" />
                            <span>{isSubmitting ? 'Guardando...' : '📋 Guardar Cotizaciones y Solicitar Aprobación'}</span>
                          </button>
                        )}

                        {/* Botón Gerencia: Aprobar Compra en Firme (Task 3.7) */}
                        {canAprobar && (
                          <button
                            type="button"
                            onClick={() => handleSubmitWithAction('APPROVE_PURCHASE')}
                            disabled={isSubmitting || lines.length === 0 || allLinesZero}
                            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-lg text-xs flex items-center gap-1.5 transition disabled:opacity-50 shadow-sm active:scale-95"
                            title="Aprueba montos presupuestarios y emite la orden 'En Compra', activando tránsito en rampa"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            <span>{isSubmitting ? 'Aprobando...' : '✅ Aprobar Compra y Emitir Orden en Firme'}</span>
                          </button>
                        )}

                        {/* Botón Delegación Operativa: Eduardo Catalá (Task 3.7) */}
                        {canDelegar && (
                          <button
                            type="button"
                            onClick={() => {
                              setDelegationReason('');
                              setShowDelegationModal(true);
                            }}
                            disabled={isSubmitting || lines.length === 0 || allLinesZero}
                            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-lg text-xs flex items-center gap-1.5 transition disabled:opacity-50 shadow-sm active:scale-95"
                            title="Autorización extraordinaria por contingencia operativa de taller"
                          >
                            <AlertTriangle className="w-4 h-4" />
                            <span>{isSubmitting ? 'Procesando...' : '⚡ Autorizar por Delegación de Operaciones'}</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      details?.oab.estadoGeneral !== 'Cancelada' && (
                        <a
                          href={`/?rampa=true&folio=${encodeURIComponent(details.oab.folio)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 transition shadow"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>Ir a Terminal de Rampa</span>
                        </a>
                      )
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Modal Interactivo de Anulación / Descarte Soberano */}
        {showCancelModal && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-surface border border-red-500/60 rounded-xl p-5 max-w-md w-full space-y-4 shadow-2xl animate-in fade-in">
              <div className="flex items-center gap-2 text-red-400 font-bold text-sm">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <span>
                  {details?.oab.estadoGeneral === 'Borrador'
                    ? 'Descarte de Borrador de Orden'
                    : 'Anulación Soberana de Orden de Abastecimiento'}
                </span>
              </div>
              <p className="text-xs text-slate-300">
                {details?.oab.estadoGeneral === 'Borrador' ? (
                  <>
                    ¿Estás seguro de que deseas descartar el borrador <strong className="font-mono text-purple-400">{details?.oab.folio}</strong>?
                    Esta acción marcará el borrador como cancelado en Notion ERP y liberará el flujo.
                  </>
                ) : (
                  <>
                    ¿Estás seguro de que deseas anular la orden <strong className="font-mono text-amber-400">{details?.oab.folio}</strong>?
                    Esta acción marcará como cancelada la cabecera y todas sus líneas en Notion, emitirá una alerta a Telegram y garantizará tránsito cero.
                  </>
                )}
              </p>
              <div>
                <label htmlFor="rev_cancel_reason" className="block text-[11px] uppercase text-slate-400 font-semibold mb-1">
                  Motivo {details?.oab.estadoGeneral === 'Borrador' ? 'del Descarte:' : 'Obligatorio de Anulación:'}
                </label>
                <textarea
                  id="rev_cancel_reason"
                  name="rev_cancel_reason"
                  aria-label="Motivo obligatorio de anulación"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={
                    details?.oab.estadoGeneral === 'Borrador'
                      ? 'Ej: Borrador descartado por cambio de presupuesto o duplicidad...'
                      : 'Ej: Rechazada por Gerencia Magaly por falta de presupuesto o cambio de diseño...'
                  }
                  className="w-full bg-page border border-borderSubtle text-xs p-2.5 rounded text-slate-200 focus:border-red-500 focus:outline-none"
                />
                <div className="text-[10px] text-slate-500 mt-1">Mínimo 5 caracteres requeridos para trazabilidad forense.</div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCancelModal(false)}
                  className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200"
                  disabled={isCanceling}
                >
                  Regresar
                </button>
                <button
                  type="button"
                  onClick={handleCancelOAB}
                  disabled={isCanceling || cancelReason.trim().length < 5}
                  className="px-4 py-1.5 bg-red-600 hover:bg-red-500 text-white font-bold text-xs rounded transition disabled:opacity-50 flex items-center gap-1.5"
                >
                  {isCanceling
                    ? (details?.oab.estadoGeneral === 'Borrador' ? 'Descartando en Notion...' : 'Anulando en Notion...')
                    : (details?.oab.estadoGeneral === 'Borrador' ? 'Confirmar Descarte' : 'Confirmar Anulación')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Búsqueda y Asignación de Pedido / Obra MTO (Fase 11.3) */}
        {isOrderSearchOpen && (
          <OrderSearchModal
            isOpen={isOrderSearchOpen}
            onClose={() => {
              setIsOrderSearchOpen(false);
              setActiveLineForOrder(null);
            }}
            onSelectOrder={(order: OrderReference) => {
              if (activeLineForOrder) {
                setLines(prev => prev.map(l => {
                  if (l.solicitudId === activeLineForOrder) {
                    const isStk = !order.id || order.codigo === 'STK' || (order.proyecto && order.proyecto.toLowerCase().includes('stock general'));
                    if (isStk) {
                      return {
                        ...l,
                        pedidoId: undefined,
                        proyectoId: undefined,
                        proyectoNombre: undefined,
                        tiendaNombre: undefined,
                        clienteNombre: undefined
                      };
                    }
                    return {
                      ...l,
                      pedidoId: order.id,
                      proyectoId: order.proyectoId || order.id,
                      tiendaNombre: order.proyecto,
                      clienteNombre: order.cliente,
                      proyectoNombre: order.cliente
                        ? `${order.codigo} • Tienda: ${order.proyecto} • Cliente: ${order.cliente}`
                        : `${order.codigo} - ${order.proyecto}`
                    };
                  }
                  return l;
                }));
              }
              setIsOrderSearchOpen(false);
              setActiveLineForOrder(null);
            }}
          />
        )}

        {/* Modal Selector de Versión de Reimpresión (PLAN-015 - Fase 3) */}
        {showVersionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 no-print">
            <div className="bg-surface border border-borderSubtle rounded-xl max-w-md w-full p-6 space-y-4 shadow-2xl animate-in fade-in zoom-in-95">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-sky-500/20 text-sky-400 border border-sky-500/40">
                  <Printer className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-100">Seleccionar Versión para Imprimir</h3>
                  <p className="text-xs text-slate-400">
                    Folio: <span className="font-mono text-brand-400 font-bold">{details?.oab.folio}</span> ({details?.oab.estadoGeneral})
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Esta orden ya cuenta con gestión gerencial o de compras. Puedes elegir qué formato de la Hoja Viajera Carta deseas imprimir:
              </p>

              <div className="space-y-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => handleSelectPrintVersion(false)}
                  className="w-full text-left p-3.5 rounded-lg border border-borderSubtle bg-surfaceHigh hover:bg-surfaceHighest hover:border-slate-500 transition group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-200 group-hover:text-white flex items-center gap-2">
                      <span>📄 Versión Original en Blanco</span>
                    </span>
                    <span className="text-[10px] font-mono text-slate-400 bg-surface px-1.5 py-0.5 rounded border border-borderSubtle">Original</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Solo cantidades solicitadas con casillas en blanco para firmas a lápiz de Magaly y Almacén.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleSelectPrintVersion(true)}
                  className="w-full text-left p-3.5 rounded-lg border border-emerald-500/40 bg-emerald-950/20 hover:bg-emerald-900/30 hover:border-emerald-400 transition group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-300 group-hover:text-emerald-200 flex items-center gap-2">
                      <span>📋 Versión Aprobada con Cotización</span>
                      <span className="text-[9px] uppercase font-bold px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Recomendado</span>
                    </span>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950 px-1.5 py-0.5 rounded border border-emerald-500/30">Aprobada</span>
                  </div>
                  <p className="text-[11px] text-slate-300 mt-1">
                    Incluye proveedor adjudicado, cotización, fecha estimada de entrega y cantidades aprobadas por Gerencia.
                  </p>
                </button>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowVersionModal(false)}
                  className="px-4 py-2 bg-surface hover:bg-surfaceHigh text-slate-400 hover:text-slate-200 text-xs font-medium rounded-lg transition"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hoja Viajera Carta Imprimible (PLAN-015 - Fase 3) */}
        {showPrintSheet && printHeader && (
          <PrintSheetOAB
            header={printHeader}
            lineas={printLines}
            isApprovedVersion={printIsApprovedVersion}
            approvedDetails={{
              proveedor: proveedorNombre || details?.oab.proveedor,
              cotizacion: numeroCotizacion || details?.oab.cotizacion,
              fechaEstimadaEntrega: fechaEstimadaEntrega || details?.oab.fechaEstimadaEntrega,
              notasCompras: notasCompras || details?.oab.notasCompras
            }}
            onClose={() => setShowPrintSheet(false)}
          />
        )}

        {/* Modal de Justificación Obligatoria para Delegación Operativa (SoD - PLAN-035) */}
        {showDelegationModal && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-surface border border-amber-500/60 rounded-xl p-5 max-w-lg w-full space-y-4 shadow-2xl animate-in fade-in">
              <div className="flex items-center gap-2 text-amber-400 font-bold text-sm">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <span>Autorización de Compra por Delegación de Operaciones</span>
              </div>
              
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-300 space-y-1">
                <p className="font-semibold">⚠️ Protocolo de Segregación de Funciones (SoD):</p>
                <p className="text-slate-300">
                  Estás a punto de autorizar la orden <strong className="font-mono text-amber-400">{details?.oab.folio}</strong> por 
                  delegación de operaciones. Para garantizar la trazabilidad contable ante Gerencia, debes ingresar el motivo operativo que justifica emitir la orden directamente a compras.
                </p>
              </div>

              <div>
                <label htmlFor="delegation_reason" className="block text-xs font-semibold text-slate-300 mb-1">
                  Motivo de Contingencia / Justificación Operativa: <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="delegation_reason"
                  name="delegation_reason"
                  rows={3}
                  value={delegationReason}
                  onChange={(e) => setDelegationReason(e.target.value)}
                  placeholder="Ej: Material crítico para entrega de proyecto Tienda X, autorizado por parada técnica inminente..."
                  className="w-full bg-surfaceHigh border border-borderSubtle rounded-lg p-2.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-500 font-sans"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDelegationModal(false)}
                  className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 rounded hover:bg-surfaceHighest transition"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!delegationReason.trim() || delegationReason.trim().length < 5) {
                      alert('Debe especificar un motivo de al menos 5 caracteres para la delegación.');
                      return;
                    }
                    handleSubmitWithAction('APPROVE_PURCHASE', delegationReason);
                  }}
                  disabled={!delegationReason.trim() || delegationReason.trim().length < 5 || isSubmitting}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-lg text-xs flex items-center gap-1.5 transition disabled:opacity-50 shadow"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Confirmar y Emitir Orden</span>
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

